/**
 * Create OpenClaw Sandbox page component.
 * Simplified sandbox creation with OpenClaw preset.
 *
 * @module pages/CreateOpenClawSandboxPage
 */

import { useState, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Terminal, ExternalLink } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Select,
  LabelsEditor,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import { useCreateSandbox, useSandboxGroups, useSandboxGroup } from '@/hooks';

interface FormState {
  labels: Record<string, string>;
  sandboxGroupId: string;
}

const initialFormState: FormState = {
  labels: {},
  sandboxGroupId: '',
};

/**
 * Create OpenClaw sandbox page
 */
export function CreateOpenClawSandboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMutation = useCreateSandbox();
  const { data: sandboxGroups, isLoading: sandboxGroupsLoading } = useSandboxGroups(1, 100);

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  // Get groupId from URL params if coming from a group page
  const preselectedGroupId = searchParams.get('groupId') || '';

  // Fetch preselected group details to inherit connectors
  const { data: preselectedGroup } = useSandboxGroup(preselectedGroupId || undefined);

  const [formState, setFormState] = useState<FormState>(() => ({
    ...initialFormState,
    sandboxGroupId: preselectedGroupId,
  }));

  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof FormState, value: string | Record<string, string>) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Commit any uncommitted label draft
    labelsEditorRef.current?.commitDraft();
    const finalLabels = formState.labels;

    try {
      const sandbox = await createMutation.mutateAsync({
        presetSandboxType: 'OpenClaw',
        labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
        sandboxGroupId: formState.sandboxGroupId || undefined,
        // Inherit connections from the selected group
        connections: preselectedGroup?.connections,
      });

      // Navigate based on whether we came from a group
      if (formState.sandboxGroupId) {
        navigate(`/sandbox-groups/${encodeURIComponent(formState.sandboxGroupId)}`);
      } else {
        navigate(`/sandboxes/${encodeURIComponent(sandbox.id)}`);
      }
    } catch (err: unknown) {
      console.error('Failed to create sandbox:', err);
      const axiosErr = err as {
        response?: { data?: { detail?: string; message?: string } };
        message?: string;
      };
      const errorMessage =
        axiosErr.response?.data?.detail ||
        axiosErr.response?.data?.message ||
        axiosErr.message ||
        'Unknown error';
      setError(`Failed to create sandbox: ${errorMessage}`);
    }
  };

  const isSubmitting = createMutation.isPending;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Create OpenClaw Sandbox"
        description="Create a sandbox with OpenClaw AI agent pre-installed for AI coding and automation"
        breadcrumbs={[
          { label: 'Sandboxes', href: '/sandboxes' },
          { label: 'Create OpenClaw Sandbox' },
        ]}
      />

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader title="Configuration" description="Configure your OpenClaw sandbox" />
          <CardBody className="space-y-6">
            {/* Preset Info */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Terminal className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-900 dark:text-amber-100">
                    OpenClaw AI Agent Sandbox
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    A development sandbox with OpenClaw AI agent pre-installed. Access the web
                    dashboard or connect via terminal for AI-powered coding and automation tasks.
                  </p>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 list-disc list-inside space-y-1">
                    <li>4 CPU cores, 8GB RAM, 10GB disk</li>
                    <li>OpenClaw agent pre-installed</li>
                    <li>Web dashboard on port 18789</li>
                    <li>GitHub CLI (gh) available</li>
                  </ul>
                  <a
                    href="https://docs.openclaw.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-amber-600 dark:text-amber-400 hover:underline mt-2"
                  >
                    Learn more about OpenClaw <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>

            {/* Sandbox Group */}
            <div>
              <Select
                label="Sandbox Group (Optional)"
                options={[
                  { value: '', label: 'No group' },
                  ...(sandboxGroups?.map((group) => {
                    const name =
                      group.labels && typeof group.labels === 'object'
                        ? (group.labels as Record<string, string>)['name'] || group.id
                        : group.id;
                    return { value: group.id, label: name };
                  }) || []),
                ]}
                value={formState.sandboxGroupId}
                onChange={(e) => handleInputChange('sandboxGroupId', e.target.value)}
                disabled={sandboxGroupsLoading || !!preselectedGroupId}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Optionally associate this sandbox with a group. Connections from the group will be
                inherited.
              </p>
            </div>

            {/* Labels */}
            <div>
              <LabelsEditor
                ref={labelsEditorRef}
                value={formState.labels}
                onChange={(labels) => handleInputChange('labels', labels)}
                label="Labels (Optional)"
              />
            </div>
          </CardBody>
          <CardFooter className="flex justify-between">
            <Link
              to={
                preselectedGroupId
                  ? `/sandbox-groups/${encodeURIComponent(preselectedGroupId)}`
                  : '/sandboxes'
              }
            >
              <Button
                type="button"
                variant="secondary"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={isSubmitting} leftIcon={<Save className="h-4 w-4" />}>
              {isSubmitting ? 'Creating...' : 'Create Sandbox'}
            </Button>
          </CardFooter>
        </Card>

        {error && (
          <Card className="border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
            <CardBody>
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </CardBody>
          </Card>
        )}
      </form>
    </div>
  );
}
